/**
 * Manage jurisdictions & permit types — the "permit paths" config the rest of the Permits feature
 * reads. Owner/office only (mutations are gated server-side; a 403 surfaces as an error here).
 *
 * Add a jurisdiction (city/county/state + optional portal), then add permit types under it. These
 * populate the "Prepare application" picker and the permits hub. BEAM still never submits/attests —
 * this is just the reference data. NEVER imports @smartbox/* (mobile invariant 1). No raw style values.
 */
import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listJurisdictions,
  listPermitTypes,
  createJurisdiction,
  createPermitType,
  type CreateJurisdictionRequest,
  type CreatePermitTypeRequest,
} from '../../../src/api/permits';
import { Screen, Text, Card, Button, Field, Select } from '../../../src/components/ui';
import { theme } from '../../../src/theme';
import { extractErrorMessage } from '../../../src/lib/format';

const LEVELS = [
  { value: 'city', label: 'City' },
  { value: 'county', label: 'County' },
  { value: 'state', label: 'State' },
];
const PORTALS = [
  { value: 'manual', label: 'Manual (no portal)' },
  { value: 'energov', label: 'EnerGov' },
  { value: 'munis', label: 'Munis' },
  { value: 'opengov', label: 'OpenGov' },
];

export default function ManagePermitsScreen() {
  const queryClient = useQueryClient();

  const jurisdictionsQuery = useQuery({ queryKey: ['jurisdictions'], queryFn: listJurisdictions });
  const typesQuery = useQuery({ queryKey: ['permit-types'], queryFn: listPermitTypes });

  // --- Add jurisdiction form ---
  const [jName, setJName] = useState('');
  const [jLevel, setJLevel] = useState<string | null>('county');
  const [jPortalUrl, setJPortalUrl] = useState('');
  const [jPortal, setJPortal] = useState<string | null>('manual');
  const [jError, setJError] = useState<string | null>(null);

  const addJurisdiction = useMutation({
    mutationFn: (body: CreateJurisdictionRequest) => createJurisdiction(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['jurisdictions'] });
      setJName('');
      setJPortalUrl('');
      setJError(null);
    },
    onError: (err) => setJError(extractErrorMessage(err, 'Could not add the jurisdiction.')),
  });

  const onAddJurisdiction = () => {
    setJError(null);
    if (!jName.trim()) return setJError('Enter a jurisdiction name.');
    if (!jLevel) return setJError('Choose a level.');
    const body: CreateJurisdictionRequest = { name: jName.trim(), level: jLevel as CreateJurisdictionRequest['level'] };
    if (jPortalUrl.trim()) body.portal_url = jPortalUrl.trim();
    if (jPortal) body.portal_integration = jPortal as CreateJurisdictionRequest['portal_integration'];
    addJurisdiction.mutate(body);
  };

  // --- Add permit type form ---
  const [pJurisdictionId, setPJurisdictionId] = useState<string | null>(null);
  const [pName, setPName] = useState('');
  const [pCode, setPCode] = useState('');
  const [pBaseFee, setPBaseFee] = useState('');
  const [pNocThreshold, setPNocThreshold] = useState('');
  const [pError, setPError] = useState<string | null>(null);

  const addPermitType = useMutation({
    mutationFn: (body: CreatePermitTypeRequest) => createPermitType(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['permit-types'] });
      setPName('');
      setPCode('');
      setPBaseFee('');
      setPNocThreshold('');
      setPError(null);
    },
    onError: (err) => setPError(extractErrorMessage(err, 'Could not add the permit type.')),
  });

  const onAddPermitType = () => {
    setPError(null);
    if (!pJurisdictionId) return setPError('Choose a jurisdiction.');
    if (!pName.trim()) return setPError('Enter a permit type name.');
    const body: CreatePermitTypeRequest = { jurisdiction_id: pJurisdictionId, name: pName.trim() };
    if (pCode.trim()) body.code = pCode.trim();
    const fee = Number.parseFloat(pBaseFee);
    if (pBaseFee.trim() && Number.isFinite(fee)) body.base_fee = fee;
    const noc = Number.parseFloat(pNocThreshold);
    if (pNocThreshold.trim() && Number.isFinite(noc)) body.noc_required_threshold = noc;
    addPermitType.mutate(body);
  };

  const jurisdictions = jurisdictionsQuery.data ?? [];
  const types = typesQuery.data ?? [];
  const jurisdictionName = (id: string | null) => jurisdictions.find((j) => j.id === id)?.name ?? '—';

  return (
    <Screen scroll>
      <Text variant="title">Jurisdictions & permit types</Text>
      <Card>
        <Text variant="small" color="secondary">
          Add the cities/counties you pull permits in and the permit types they offer. These feed the
          “Prepare application” picker. BEAM never submits or files — this is reference data only.
        </Text>
      </Card>

      {/* ---- Jurisdictions ---- */}
      <Text variant="label" color="secondary" style={styles.section}>
        Jurisdictions
      </Text>
      {jurisdictionsQuery.isLoading ? (
        <ActivityIndicator color={theme.color.action.primary} />
      ) : jurisdictions.length === 0 ? (
        <Card>
          <Text color="secondary">None yet — add your first one below.</Text>
        </Card>
      ) : (
        jurisdictions.map((j) => (
          <Card key={j.id}>
            <Text variant="bodyStrong">{j.name}</Text>
            <Text variant="small" color="secondary">
              {(j.level ?? 'jurisdiction')}{j.portal_integration && j.portal_integration !== 'manual' ? ` · ${j.portal_integration}` : ''}
            </Text>
          </Card>
        ))
      )}

      <Card>
        <Text variant="bodyStrong">Add a jurisdiction</Text>
        <Field label="Name" placeholder="e.g. City of Fernandina Beach" value={jName} onChangeText={setJName} accessibilityLabel="Jurisdiction name" />
        <Text variant="label" color="secondary" style={styles.fieldLabel}>Level</Text>
        <Select options={LEVELS} value={jLevel} onChange={setJLevel} />
        <Text variant="label" color="secondary" style={styles.fieldLabel}>Permitting portal</Text>
        <Select options={PORTALS} value={jPortal} onChange={setJPortal} />
        <Field label="Portal URL (optional)" placeholder="https://…" value={jPortalUrl} onChangeText={setJPortalUrl} autoCapitalize="none" autoCorrect={false} accessibilityLabel="Portal URL" />
        {jError ? <Text variant="bodyStrong" color="danger" accessibilityRole="alert">{jError}</Text> : null}
        <Button title="Add jurisdiction" onPress={onAddJurisdiction} loading={addJurisdiction.isPending} disabled={addJurisdiction.isPending} />
      </Card>

      {/* ---- Permit types ---- */}
      <Text variant="label" color="secondary" style={styles.section}>
        Permit types
      </Text>
      {types.length === 0 ? (
        <Card>
          <Text color="secondary">None yet{jurisdictions.length === 0 ? ' — add a jurisdiction first.' : '.'}</Text>
        </Card>
      ) : (
        types.map((t) => (
          <Card key={t.id}>
            <Text variant="bodyStrong">{t.code ? `${t.name} (${t.code})` : t.name}</Text>
            <Text variant="small" color="secondary">{jurisdictionName(t.jurisdiction_id)}</Text>
          </Card>
        ))
      )}

      <Card>
        <Text variant="bodyStrong">Add a permit type</Text>
        {jurisdictions.length === 0 ? (
          <Text color="secondary">Add a jurisdiction first.</Text>
        ) : (
          <>
            <Text variant="label" color="secondary" style={styles.fieldLabel}>Jurisdiction</Text>
            <Select options={jurisdictions.map((j) => ({ value: j.id, label: j.name }))} value={pJurisdictionId} onChange={setPJurisdictionId} />
            <Field label="Name" placeholder="e.g. Building Permit" value={pName} onChangeText={setPName} accessibilityLabel="Permit type name" />
            <Field label="Code (optional)" placeholder="e.g. BLDG" value={pCode} onChangeText={setPCode} autoCapitalize="characters" autoCorrect={false} accessibilityLabel="Permit type code" />
            <Field label="Base fee (optional)" placeholder="0.00" value={pBaseFee} onChangeText={setPBaseFee} keyboardType="decimal-pad" accessibilityLabel="Base fee" />
            <Field label="NOC required above $ (optional)" placeholder="e.g. 5000" value={pNocThreshold} onChangeText={setPNocThreshold} keyboardType="decimal-pad" accessibilityLabel="NOC required threshold" />
            {pError ? <Text variant="bodyStrong" color="danger" accessibilityRole="alert">{pError}</Text> : null}
            <Button title="Add permit type" onPress={onAddPermitType} loading={addPermitType.isPending} disabled={addPermitType.isPending} />
          </>
        )}
      </Card>
    </Screen>
  );
}

const styles = StyleSheet.create({
  section: { marginTop: theme.spacing.lg },
  fieldLabel: { marginTop: theme.spacing.sm, marginBottom: theme.spacing.xs },
});
